import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar';
import { 
  UserIcon, 
  FileIcon, 
  BarChartIcon, 
  LocationIcon,
  PhoneIcon,
  EmailIcon,
  MenuIcon,
  CloseIcon,
  SearchIcon,
  FilterIcon
} from '../components/icons/Icons';

const MyIssues: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Mock data for user's issues
  const [allIssues, setAllIssues] = useState([
    {
      id: 'CIV-001',
      title: 'Pothole on Main Street',
      category: 'Roads',
      status: 'In Progress',
      date: '2024-01-15',
      priority: 'High',
      location: 'Main Street, Sector 5',
      description: 'Large pothole causing traffic issues and vehicle damage',
      department: 'Public Works',
      updatedAt: '2024-01-18',
      feedback: 'Work in progress, expected completion by Friday'
    },
    {
      id: 'CIV-002',
      title: 'Street Light Not Working',
      category: 'Electricity',
      status: 'Pending',
      date: '2024-01-14',
      priority: 'Medium',
      location: 'Park Avenue, Block B',
      description: 'Street light has been off for a week, creating safety concerns',
      department: 'Electricity Department',
      updatedAt: '2024-01-14',
      feedback: 'Issue registered, awaiting assignment'
    },
    {
      id: 'CIV-003',
      title: 'Garbage Collection Missed',
      category: 'Sanitation',
      status: 'Resolved',
      date: '2024-01-13',
      priority: 'Low',
      location: 'Oak Street, Area 3',
      description: 'Garbage has not been collected for 5 days',
      department: 'Sanitation Department',
      updatedAt: '2024-01-17',
      feedback: 'Issue resolved, collection completed on 17th Jan'
    },
    {
      id: 'CIV-004',
      title: 'Water Supply Issue',
      category: 'Water Supply',
      status: 'In Progress',
      date: '2024-01-12',
      priority: 'High',
      location: 'Garden Road, Phase 2',
      description: 'Irregular water supply timing for past 3 days',
      department: 'Water Department',
      updatedAt: '2024-01-16',
      feedback: 'Team dispatched, schedule adjustment in progress'
    },
    {
      id: 'CIV-005',
      title: 'Broken Park Bench',
      category: 'Public Facilities',
      status: 'Pending',
      date: '2024-01-11',
      priority: 'Low',
      location: 'Central Park, Zone A',
      description: 'Bench broken and poses safety hazard',
      department: 'Public Works',
      updatedAt: '2024-01-11',
      feedback: 'Issue registered, awaiting assessment'
    },
    {
      id: 'CIV-006',
      title: 'Drainage Blockage',
      category: 'Water Supply',
      status: 'Resolved',
      date: '2024-01-10',
      priority: 'Medium',
      location: 'Elm Street, District 2',
      description: 'Drainage blocked causing waterlogging after rains',
      department: 'Water Department',
      updatedAt: '2024-01-15',
      feedback: 'Drainage cleared, issue resolved'
    }
  ]);

  const [filteredIssues, setFilteredIssues] = useState(allIssues);

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Filter issues based on search and filters
    let result = allIssues;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(issue => 
        issue.title.toLowerCase().includes(term) ||
        issue.id.toLowerCase().includes(term) ||
        issue.location.toLowerCase().includes(term)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(issue => issue.status === statusFilter);
    }
    
    // Apply priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(issue => issue.priority === priorityFilter);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      result = result.filter(issue => issue.category === categoryFilter);
    }
    
    setFilteredIssues(result);
  }, [searchTerm, statusFilter, priorityFilter, categoryFilter, allIssues]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Resolved': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-orange-600 bg-orange-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
  };

  // Get unique categories for filter dropdown
  const categories = Array.from(new Set(allIssues.map(issue => issue.category)));

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
              </button>
              <div className="flex items-center space-x-3">
                <img src="/hero.png" alt="Logo" className="w-8 h-8" />
                <h1 className="text-xl font-semibold text-gray-900">My Issues</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.email}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="citizen"
          currentPage="my-issues"
        />

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Page Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">My Reported Issues</h2>
                  <p className="text-gray-600 text-sm sm:text-base mt-1">Track the status of issues you've reported to the civic authorities</p>
                </div>
                <div className="sm:mt-0">
                  <button 
                    onClick={() => navigate('/report-issue')}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                  >
                    Report New Issue
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                {/* Search */}
                <div className="lg:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Search</label>
                  <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by title, ID, or location..."
                      className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* Status Filter */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>
                
                {/* Priority Filter */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                
                {/* Category Filter */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-3">
                <div className="text-xs sm:text-sm text-gray-500">
                  Showing {filteredIssues.length} of {allIssues.length} issues
                </div>
                <button
                  onClick={clearFilters}
                  className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 self-start sm:self-auto"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Issues List */}
            <div className="space-y-4">
              {filteredIssues.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
                  <FileIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No issues found</h3>
                  <p className="text-gray-500 text-sm sm:text-base mb-6">You haven't reported any issues matching the current filters.</p>
                  <button
                    onClick={() => navigate('/report-issue')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                  >
                    Report Your First Issue
                  </button>
                </div>
              ) : (
                filteredIssues.map((issue) => (
                  <div key={issue.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900">{issue.title}</h3>
                              <span className="text-xs text-gray-500">({issue.id})</span>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(issue.status)}`}>
                                {issue.status}
                              </span>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(issue.priority)}`}>
                                {issue.priority} Priority
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-2">{issue.description}</p>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                              <span>{issue.category}</span>
                              <span>{issue.location}</span>
                              <span>Reported: {issue.date}</span>
                              <span>Updated: {issue.updatedAt}</span>
                            </div>
                          </div>
                        </div>
                        
                        {issue.feedback && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs sm:text-sm text-blue-800">
                              <span className="font-medium">Official Feedback:</span> {issue.feedback}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="sm:ml-6">
                        <button
                          onClick={() => navigate(`/complaint/${issue.id}`)}
                          className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-lg">
                    <FileIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Total Issues</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{allIssues.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <div className="flex items-center">
                  <div className="bg-orange-100 p-2 sm:p-3 rounded-lg">
                    <BarChartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">In Progress</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {allIssues.filter(issue => issue.status === 'In Progress').length}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 p-2 sm:p-3 rounded-lg">
                    <BarChartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Resolved</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {allIssues.filter(issue => issue.status === 'Resolved').length}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <div className="flex items-center">
                  <div className="bg-red-100 p-2 sm:p-3 rounded-lg">
                    <BarChartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">High Priority</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {allIssues.filter(issue => issue.priority === 'High').length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MyIssues;